import { Box, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import { ADMIN_ENV } from '@/lib/env';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { fontSizes } from '@liminal/brand/scale';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';
import { getErrorMessage } from '@/errors/messages';

const PageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: 16,
});

const SERVICES = [
  'users',
  'wallets',
  'savings',
  'ledger',
  'kyc',
  'investing',
  'ramps',
  'notifications',
  'scheduled-payments',
] as const;

function useAdminHealth() {
  return useQuery({
    queryKey: ['system', 'health'],
    queryFn: () => adminClient.healthCheck(),
    refetchInterval: 30_000,
  });
}

export function SystemPage() {
  const { data, isLoading, error } = useAdminHealth();

  return (
    <>
      <PageHeader title="System Health" autoTitleCase={false} />
      <PageRoot>

      {/* Admin API Status */}
      <Card variant="elevated">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>Admin API</Typography>
            {isLoading ? (
              <Skeleton width={80} height={24} />
            ) : (
              <StatusPill
                label={data?.status === 'ok' ? 'HEALTHY' : 'UNHEALTHY'}
                tone={data?.status === 'ok' ? 'success' : 'error'}
              />
            )}
          </Box>
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {getErrorMessage(error)}
            </Typography>
          )}
      </Card>

      {/* Downstream Services */}
      <Typography variant="h6" fontWeight={600}>Downstream Services</Typography>
      <Typography variant="body2" color="text.disabled">
        Service health is reported by the admin API's gRPC health checks. If the admin API is healthy, all downstream connections were established successfully at startup.
      </Typography>

      <Grid>
        {SERVICES.map(svc => (
          <Card
            key={svc}
            variant="elevated"
            sx={{
              borderLeft: (theme) =>
                `4px solid ${data?.status === 'ok' ? theme.palette.success.main : theme.palette.error.main}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                {svc.replace(/-/g, ' ')}
              </Typography>
              {isLoading ? (
                <Skeleton width={60} height={20} />
              ) : (
                <StatusPill
                  label={data?.status === 'ok' ? 'OK' : 'DOWN'}
                  tone={data?.status === 'ok' ? 'success' : 'error'}
                  size="sm"
                />
              )}
            </Box>
          </Card>
        ))}
      </Grid>

      {/* Info */}
      <Card variant="elevated">
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Configuration</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Environment: {ADMIN_ENV}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs }}>
              API: {import.meta.env.VITE_ADMIN_API_URL || '(default proxy)'}
            </Typography>
          </Box>
      </Card>
      </PageRoot>
    </>
  );
}
